/**
 * SignaturePremiumBadge - Display signature premium value estimate
 * MDM-UNIFIED-001 Phase 8: Signature Premium Index
 */
import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, Minus, Star, Award, Shield,
  DollarSign, Info, Loader2
} from 'lucide-react';
import { getSignaturePremiumEstimate } from '../../services/vaultApi';

// Premium tier colors and labels
const TIER_CONFIG = {
  elite: {
    color: 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white',
    label: 'Elite',
    icon: Star,
    description: 'Doubles or more the value',
  },
  premium: {
    color: 'bg-gradient-to-r from-purple-500 to-purple-600 text-white',
    label: 'Premium',
    icon: Award,
    description: '50%+ value increase',
  },
  notable: {
    color: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white',
    label: 'Notable',
    icon: Shield,
    description: '25-50% value increase',
  },
  moderate: {
    color: 'bg-green-500/80 text-white',
    label: 'Moderate',
    icon: DollarSign,
    description: '10-25% value increase',
  },
  minimal: {
    color: 'bg-zinc-500/80 text-white',
    label: 'Minimal',
    icon: DollarSign,
    description: 'Less than 10% increase',
  },
  unknown: {
    color: 'bg-zinc-600/50 text-zinc-300',
    label: 'Unknown',
    icon: Info,
    description: 'Not enough data',
  },
};

// Confidence colors
const CONFIDENCE_COLORS = {
  high: 'text-green-400',
  medium: 'text-yellow-400',
  low: 'text-orange-400',
  estimated: 'text-zinc-400',
};

// Trend icons
const TREND_ICONS = {
  up: { icon: TrendingUp, color: 'text-green-400' },
  down: { icon: TrendingDown, color: 'text-red-400' },
  stable: { icon: Minus, color: 'text-zinc-400' },
};

/**
 * Compact badge showing premium tier
 */
export function SignaturePremiumTierBadge({ tier, size = 'sm' }) {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.unknown;
  const TierIcon = config.icon;

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded font-medium ${config.color} ${sizeClasses[size]}`}>
      <TierIcon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

/**
 * Premium percentage display with trend
 */
export function SignaturePremiumPct({ pct, trend, showTrend = true }) {
  const TrendIcon = trend ? TREND_ICONS[trend]?.icon : null;
  const trendColor = trend ? TREND_ICONS[trend]?.color : '';

  return (
    <div className="flex items-center gap-1">
      <span className="text-orange-400 font-medium">+{pct?.toFixed(0)}%</span>
      {showTrend && TrendIcon && (
        <TrendIcon className={`w-3 h-3 ${trendColor}`} />
      )}
    </div>
  );
}

/**
 * Full premium badge with estimate lookup
 */
export default function SignaturePremiumBadge({
  signerName,
  creatorId,
  witnessType,
  compact = false,
  showDetails = true,
}) {
  const [estimate, setEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (signerName) {
      loadEstimate();
    }
  }, [signerName, creatorId, witnessType]);

  const loadEstimate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getSignaturePremiumEstimate(signerName, {
        creator_id: creatorId,
        witness_type: witnessType,
      });
      setEstimate(result);
    } catch (err) {
      console.error('Failed to load premium estimate:', err);
      setError('Unable to load premium data');
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-1 text-xs text-zinc-400">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  if (error || !estimate) {
    return null;
  }

  const tierConfig = TIER_CONFIG[estimate.premium_tier] || TIER_CONFIG.unknown;

  if (compact) {
    return (
      <div className="relative">
        <button
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-default"
        >
          <SignaturePremiumTierBadge tier={estimate.premium_tier} size="xs" />
        </button>

        {showTooltip && (
          <div className="absolute z-50 bottom-full left-0 mb-1 p-2 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg text-xs w-48">
            <div className="font-medium text-white mb-1">{signerName}</div>
            <div className="text-orange-400 font-medium mb-1">
              +{estimate.estimated_premium_pct?.toFixed(0)}% Premium
            </div>
            <div className={`text-xs ${CONFIDENCE_COLORS[estimate.confidence]}`}>
              {estimate.confidence === 'estimated' ? 'Estimated' : `${estimate.confidence} confidence`}
              {estimate.sample_count > 0 && ` (${estimate.sample_count} samples)`}
            </div>
            {estimate.is_high_impact && (
              <div className="text-yellow-400 flex items-center gap-1 mt-1">
                <Star className="w-3 h-3" />
                High-Impact Creator
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50">
      <div className="flex items-center justify-between mb-2">
        <SignaturePremiumTierBadge tier={estimate.premium_tier} />
        {estimate.is_high_impact && (
          <div className="flex items-center gap-1 text-yellow-400 text-xs">
            <Star className="w-3 h-3" />
            High-Impact
          </div>
        )}
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-bold text-orange-400">
          +{estimate.estimated_premium_pct?.toFixed(0)}%
        </span>
        {estimate.trend_direction && (
          <SignaturePremiumPct pct={0} trend={estimate.trend_direction} showTrend={true} />
        )}
      </div>

      {showDetails && (
        <div className="text-xs text-zinc-400 space-y-1">
          <div className={CONFIDENCE_COLORS[estimate.confidence]}>
            {estimate.confidence === 'high' && 'High confidence estimate'}
            {estimate.confidence === 'medium' && 'Medium confidence estimate'}
            {estimate.confidence === 'low' && 'Low confidence estimate'}
            {estimate.confidence === 'estimated' && 'Estimated (limited data)'}
          </div>

          {estimate.sample_count > 0 && (
            <div>Based on {estimate.sample_count} signature{estimate.sample_count !== 1 ? 's' : ''}</div>
          )}

          <div className="text-zinc-500">{tierConfig.description}</div>

          {estimate.notes && (
            <div className="text-zinc-500 italic">{estimate.notes}</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Top Premiums List Component
 */
export function TopPremiumsList({ premiums, onSelect }) {
  if (!premiums || premiums.length === 0) {
    return (
      <div className="text-center py-4 text-zinc-500 text-sm">
        No premium data available
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {premiums.map((p, index) => (
        <button
          key={p.id}
          onClick={() => onSelect?.(p)}
          className="w-full flex items-center justify-between p-2 bg-zinc-800/50 hover:bg-zinc-700/50 rounded-lg transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <span className="text-zinc-500 text-sm w-6">{index + 1}.</span>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">{p.signer_name}</span>
                {p.is_high_impact && (
                  <Star className="w-3 h-3 text-yellow-400" />
                )}
              </div>
              <div className="text-xs text-zinc-500">
                {p.witness_category.replace('_', ' ')} | {p.sample_count} samples
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <SignaturePremiumPct
              pct={p.avg_premium_pct}
              trend={p.trend_direction}
            />
            <SignaturePremiumTierBadge tier={p.premium_tier} size="xs" />
          </div>
        </button>
      ))}
    </div>
  );
}
